import type { Element as XmlEl } from "@xmpp/xml"
import { attr } from "../../../xmlUtils"
import { InvalidStanza, UnsupportedEventStanza } from "../exceptions"
import {
  isEventType,
  type MemberEventMessage,
  type RenamedEventMessage,
  type SimpleEventMessage,
} from "../../../../types"
import { NS_ATTENSI_EVENT } from "../../../constants"

function event(stanza: XmlEl) {
  return stanza.getChild("event", NS_ATTENSI_EVENT)
}

export function isEvent(stanza: XmlEl): boolean {
  return !!event(stanza)
}

export function parseEvent(stanza: XmlEl) {
  const tag = event(stanza)
  if (!tag) throw new InvalidStanza("not an event", stanza)

  const eventType = attr(tag, "type")
  // A marker with no type at all is malformed, not merely newer than us.
  if (!eventType) throw new InvalidStanza("typeless event!", stanza)
  if (!isEventType(eventType))
    throw new UnsupportedEventStanza(
      `event ${eventType} not supported!`,
      stanza
    )

  switch (eventType) {
    case "archived":
    case "unarchived":
      return parseSimpleEvent(eventType)
    case "renamed":
      return parseRenameEvent(stanza, tag)
    case "member_added":
    case "member_removed":
    case "member_left":
    case "promoted":
    case "demoted":
      return parseMemberEvent(stanza, tag, eventType)
  }
}

function parseSimpleEvent(
  eventType: SimpleEventMessage["event"]
): Pick<SimpleEventMessage, "type" | "event"> {
  return { type: "event", event: eventType }
}

/**
 * A notice missing the field its kind requires degrades rather than dropping,
 * same as an unknown kind: Costanza and Engage deploy independently, so a
 * notice shaped differently by a newer server is ordinary. The notice carries
 * a human-readable fallback body for exactly this, and dropping the message
 * would lose that too.
 */
function parseRenameEvent(
  stanza: XmlEl,
  tag: XmlEl
): Pick<RenamedEventMessage, "type" | "event" | "name"> {
  const name = attr(tag, "name")
  if (!name) throw new UnsupportedEventStanza("nameless rename event!", stanza)

  return { type: "event", event: "renamed", name }
}

function parseMemberEvent(
  stanza: XmlEl,
  tag: XmlEl,
  eventType: MemberEventMessage["event"]
): Pick<MemberEventMessage, "type" | "event" | "member"> {
  const member = attr(tag, "member")
  if (!member)
    throw new UnsupportedEventStanza("memberless member event!", stanza)

  return { type: "event", event: eventType, member }
}
